import type { RegisterOptions } from "react-hook-form";
export const isHTMLElement = (value: unknown): value is HTMLElement => {
  const owner = value ? ((value as HTMLElement).ownerDocument as Document) : 0;
  return (
    value instanceof (owner && owner.defaultView ? owner.defaultView.HTMLElement : HTMLElement)
  );
};

export type FieldValues = Record<string, any>;

/* eslint-disable @typescript-eslint/no-unused-vars */
export type FieldElement<TFieldValues extends FieldValues = FieldValues> =
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement;

export const isRadioInput = (element: FieldElement): element is HTMLInputElement =>
  element.type === "radio";

export const isFileInput = (element: FieldElement): element is HTMLInputElement =>
  element.type === "file";

export const isCheckBoxInput = (element: FieldElement): element is HTMLInputElement =>
  element.type === "checkbox";

export const isUndefined = (val: unknown): val is undefined => val === undefined;

// import {
//   InternalFieldErrors,
//   InternalFieldName,
//   ValidateResult,
// } from '../types';

export const appendErrors = (
  name: any, // InternalFieldName,
  validateAllFieldCriteria: boolean,
  errors: any, // InternalFieldErrors,
  type: string,
  message: any //ValidateResult,
) =>
  validateAllFieldCriteria
    ? {
        ...errors[name],
        types: {
          ...(errors[name] && errors[name]!.types ? errors[name]!.types : {}),
          [type]: message || true,
        },
      }
    : {};

export const isNullOrUndefined = (value: unknown): value is null | undefined => value == null;

export const isBoolean = (value: unknown): value is boolean => typeof value === "boolean";

type CheckboxFieldResult = {
  isValid: boolean;
  value: string | string[] | boolean | undefined;
};

const defaultResult: CheckboxFieldResult = {
  value: false,
  isValid: false,
};

const validResult = { value: true, isValid: true };

export const getCheckboxValue = (options?: HTMLInputElement[]): CheckboxFieldResult => {
  if (Array.isArray(options)) {
    if (options.length > 1) {
      const values = options
        .filter((option) => option && option.checked && !option.disabled)
        .map((option) => option.value);
      return { value: values, isValid: !!values.length };
    }

    return options[0].checked && !options[0].disabled
      ? // @ts-expect-error expected to work in the browser
        options[0].attributes && !isUndefined(options[0].attributes.value)
        ? isUndefined(options[0].value) || options[0].value === ""
          ? validResult
          : { value: options[0].value, isValid: true }
        : validResult
      : defaultResult;
  }

  return defaultResult;
};

type RadioFieldResult = {
  isValid: boolean;
  value: number | string | null;
};

const defaultReturn: RadioFieldResult = {
  isValid: false,
  value: null,
};

export const getRadioValue = (options?: HTMLInputElement[]): RadioFieldResult =>
  Array.isArray(options)
    ? options.reduce(
        (previous, option): RadioFieldResult =>
          option && option.checked && !option.disabled
            ? {
                isValid: true,
                value: option.value,
              }
            : previous,
        defaultReturn
      )
    : defaultReturn;

type Message = string;

export const isString = (value: unknown): value is string => typeof value === "string";
export const isMessage = (value: unknown): value is Message => isString(value);

export type ValidationValue = boolean | number | string | RegExp;

export type ValidationRule<TValidationValue extends ValidationValue = ValidationValue> =
  | TValidationValue
  | ValidationValueMessage<TValidationValue>;

export type ValidationValueMessage<TValidationValue extends ValidationValue = ValidationValue> = {
  value: TValidationValue;
  message: Message;
};

const isDateObject = (value: unknown): value is Date => value instanceof Date;

export const isObjectType = (value: unknown) => typeof value === "object";

export const isObject = <T extends object>(value: unknown): value is T =>
  !isNullOrUndefined(value) && !Array.isArray(value) && isObjectType(value) && !isDateObject(value);

export const isRegex = (value: unknown): value is RegExp => value instanceof RegExp;

export const getValueAndMessage = (validationData?: ValidationRule) =>
  isObject(validationData) && !isRegex(validationData)
    ? validationData
    : {
        value: validationData,
        message: "",
      };
export type Ref = FieldElement;

type ValidateResult = Message | Message[] | boolean | undefined;

// replaced RegisterOptions
type validationEnum =
  | "required"
  | "min"
  | "max"
  | "maxLength"
  | "minLength"
  | "validate"
  | "pattern";

export type MultipleFieldErrors = {
  [K in validationEnum]?: ValidateResult;
} & {
  [key: string]: ValidateResult;
};

export type FieldError = {
  type: LiteralUnion<validationEnum, string>;
  root?: FieldError;
  ref?: Ref;
  types?: MultipleFieldErrors;
  message?: Message;
};

export type Primitive = null | undefined | string | number | boolean | symbol | bigint;

type LiteralUnion<T extends U, U extends Primitive> = T | (U & { _?: never });

export const getValidateError = (
  result: ValidateResult,
  ref: Ref,
  type = "validate"
): FieldError | void => {
  if (
    isMessage(result) ||
    (Array.isArray(result) && result.every(isMessage)) ||
    (isBoolean(result) && !result)
  ) {
    return {
      type,
      message: isMessage(result) ? result : "",
      ref,
    };
  }
};
export type EmptyObject = { [K in string | number]: never };

export const isEmptyObject = (value: unknown): value is EmptyObject =>
  isObject(value) && !Object.keys(value).length;

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const isFunction = (value: unknown): value is Function => typeof value === "function";

const compact = <TValue>(value: TValue[]) => (Array.isArray(value) ? value.filter(Boolean) : []);

export const get = <T>(obj: T, path?: string, defaultValue?: unknown): any => {
  if (!path || !isObject(obj)) {
    return defaultValue;
  }

  const result = compact(path.split(/[,[\].]+?/)).reduce(
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    (result, key) => (isNullOrUndefined(result) ? result : result[key as keyof {}]),
    obj
  );

  return isUndefined(result) || result === obj
    ? isUndefined(obj[path as keyof T])
      ? defaultValue
      : obj[path as keyof T]
    : result;
};

export type NativeFieldValue = string | number | boolean | null | undefined | unknown[];

export type InternalFieldName = string;

export type Field = {
  // _f: {
  ref: Ref;
  name: InternalFieldName;
  refs?: HTMLInputElement[];
  mount?: boolean;
} & RegisterOptions;
// };
